import React, { useState, Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Dropdown, Toast } from "react-bootstrap";
import { ActionHandlerFunction, AdminApprovalGroup, AdminUser, ApproversInfo } from "@preveil-api";
import {
  Account, AdminErrorMessages, AdminSuccessMessages, AdminUIActionTypes, AdminWarningMessages, isSameUser, Message, MessageAnchors, MessageHandlerDisplayType, MessageToastTypes,
  PrivateRoutes, useAddOrgApprovalGroupMembersMutation, useAppDispatch, useAppSelector
} from "src/common";
import { GroupMembersTable } from "..";
import { adminActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  show_modal: boolean;
  recovery_groups: AdminApprovalGroup[];
  selected_users: AdminUser[];
  users_list: AdminUser[];
  setShowModal: Dispatch<SetStateAction<boolean>>;
  handleAction: ActionHandlerFunction;
};

// Description: This component is a modal that allows an admin to set a recovery group for an user.
function SetRecoveryGroupModalComponent(props: AllProps) {
  const { show_modal, recovery_groups, selected_users, current_account, users_list, handleAction, setShowModal } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const org_info = useAppSelector((state: RootState) => state.account.organization);
  const warning_msg_recovery_group = AdminWarningMessages.bulk_change_recovery_group;
  const [selected_recovery_group, setRecoveryGroup] = useState<AdminApprovalGroup>();
  const [addOrgApprovalGroupMembers] = useAddOrgApprovalGroupMembersMutation();
  // Description: Handle the dropdown change event
  function handleDropdownChange(event: React.MouseEvent<HTMLElement>) {
    const { id } = event.target as HTMLElement;
    const recovery_group = recovery_groups.find((group: AdminApprovalGroup) => group.approval_group_id === id);
    if (recovery_group && recovery_group?.approvers) {
      setRecoveryGroup(recovery_group);
    }
  }

  // Description: Reset and Cancel
  function handleCancel() {
    setRecoveryGroup(undefined);
    setShowModal(false);
  }

  // Description: API for assigning the recovery group to the selected users.
  // TO DO: currently, when we use the toolbar to set recovery group - it allows switching to the current recovery group which creates
  // a pending update (should we prevent this?)
  async function assignSelectedRecoveryGroup() {
    if (!!selected_recovery_group && !!org_info) {
      const { grouped_users, needs_approval, already_assigned_group, pending_users } = groupUsersByGroupId(selected_users, selected_recovery_group.approval_group_id);
      if (!_.isEmpty(grouped_users)) {
        try {
          const approvers: ApproversInfo[] = [];
          for (const approver of selected_recovery_group.approvers) {
            const user = users_list.find((user) => isSameUser(user.user_id, approver.address));
            // Note: We have to make sure we have the correct account version to ensure the recovery process works correctly.
            // If the approver is not in the users list, we can not know their account version, so we need to show an error message and stop the recovery group update.
            if (!user) {
              const message = AdminErrorMessages.error_recovery_group_approver_not_found.replace(MessageAnchors.user_id, approver.address);
              dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr, MessageToastTypes.danger)));
              reset();
              return;
            }
            approvers.push({ user_id: approver.address, account_version: user.account_version, required: false });
          }
          // await for all the requests to be completed.
          // it will go to catch block if any of the requests fails.
          const results = await Promise.allSettled(Object.entries(grouped_users).map(([group_id, users]) => {
            const current_group_version = users[0].recovery_group?.group_version || null;
            const current_group_id = group_id === "no_group" ? null : group_id;
            const user_ids = users.map((user) => user.user_id);
            return addOrgApprovalGroupMembers({
              orgId: org_info.org_id, userId: current_account.user_id,
              groupVersion: selected_recovery_group.group_version, approvalGroupUid: selected_recovery_group.approval_group_id,
              currentGroupId: current_group_id,
              currentGroupVersion: current_group_version, approvers, optionalsRequired: selected_recovery_group.required_approvers, users: user_ids
            });
          }));
          results.forEach((result) => {
            // we'd like to log the errors in case a single or more request fails.
            if (result.status === "rejected") {
              const message = AdminErrorMessages.error_set_recovery_group.replace(MessageAnchors.message_content, `${selected_users.length === 1 ? selected_users[0].display_name : selected_users.length} users`);
              dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.logger)));
            }
          });
          if (results.every(result => result.status === "fulfilled")) {
            if (needs_approval > 0) {
              const warning_message = AdminWarningMessages.warning_bulk_recovery_group_update.replace(MessageAnchors.message_content, `${needs_approval} ${needs_approval === 1 ? "user" : "users"}`);
              dispatch(uiActions.handleSetMessage(new Message(warning_message, MessageHandlerDisplayType.toastr, MessageToastTypes.warning)));
            } else {
              // pending users also include invited users
              const total_success_users = selected_users.length - pending_users;
              let template = "";
              if (total_success_users === 1 && selected_users.length > 1) {
                template = selected_users.filter((user) => user.status !== "pending")[0].display_name;
              } else if (total_success_users > 1) { 
                template = `${total_success_users} users`;
              } else {
                template = selected_users[0].display_name;
              }
              const success_message = AdminSuccessMessages.success_set_recovery_group.replace(MessageAnchors.message_content, `${template}`);
              dispatch(uiActions.handleSetMessage(new Message(success_message)));
            }
          }
          dispatch(adminActions.setSelected([]));
          handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
          setRecoveryGroup(undefined);
          setShowModal(false);
        } catch (error) {
          handleError(error);
        }
      } else {
        if (selected_users.length === already_assigned_group) {
          const message = AdminWarningMessages.warning_already_assigned_recovery_group.replace(MessageAnchors.message_content, "the selected users");
          dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr, MessageToastTypes.warning)));
          reset();
        } else if (selected_users.length === pending_users) {
          const message = AdminWarningMessages.warning_pending_users_recovery_group;
          dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr, MessageToastTypes.warning)));
          reset();
        } else if (already_assigned_group + pending_users === selected_users.length) {
          const message = AdminWarningMessages.warning_pending_users_recovery_group;
          dispatch(uiActions.handleSetMessage(new Message(message, MessageHandlerDisplayType.toastr, MessageToastTypes.warning)));
          reset();
        } else {
          handleError("Selected users payload is empty.");
        }
      }
    }
  }

  // Description: reset recovery group, selected and close the modal
  function reset() {
    setRecoveryGroup(undefined);
    setShowModal(false);
    dispatch(adminActions.setSelected([]));
  }

  // Description: Group users by group id
  // if the user is already assigned to the group or if it is a pending user, we skip the user
  function groupUsersByGroupId(users: AdminUser[], new_group_id: string): { grouped_users: { [key: string]: AdminUser[] }, needs_approval: number, already_assigned_group: number, pending_users: number } {
    let needs_approval: number = 0;
    let already_assigned_group: number = 0;
    let pending_users: number = 0;
    const grouped_users: { [key: string]: AdminUser[] } = {};
    for (const user of users) {
      if (user.recovery_group?.approval_group_id === new_group_id) {
        already_assigned_group += 1;
        continue;
      }
      if (user.status === "pending" || user.status === "invited") {
        pending_users += 1;
        continue;
      }
      if (!!user.recovery_group) {
        needs_approval += 1;
      }
      const group_key = user.recovery_group?.approval_group_id || "no_group";
      if (!grouped_users[group_key]) {
        grouped_users[group_key] = [];
      }
      grouped_users[group_key].push(user);
    }

    return { grouped_users, needs_approval, already_assigned_group, pending_users };
  }

  function handleError(stack: any) {
    setRecoveryGroup(undefined);
    const error_message = AdminErrorMessages.error_set_recovery_group.replace(MessageAnchors.message_content, `${selected_users.length === 1 ? selected_users[0].display_name : selected_users.length} users`);
    dispatch(uiActions.handleRequestErrors(new Message(error_message, MessageHandlerDisplayType.toastr), stack));
  }

  function navigateToApprovalGroup() {
    navigate(`/admin/${PrivateRoutes.approval_groups_route}`);
  }

  return <Modal size="lg" show={show_modal} onHide={handleCancel} centered aria-label="Set User Recovery Group">
    <Modal.Header closeButton>
      <Modal.Title>Set User Recovery Group</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {recovery_groups.length > 0 ?
        <>
          <Dropdown className="pv-dropdown">
            <Dropdown.Toggle variant="outline-secondary" id="dropdown-basic">
              {!!selected_recovery_group ? selected_recovery_group?.name : "Select Recovery Group"}
            </Dropdown.Toggle>
            <Dropdown.Menu align="start">
              {recovery_groups.map((group: AdminApprovalGroup) => {
                const { approval_group_id, name } = group;
                return <Dropdown.Item key={approval_group_id} id={approval_group_id} onClick={handleDropdownChange}>{name}</Dropdown.Item>;
              })}
            </Dropdown.Menu>
          </Dropdown>
          {!!selected_recovery_group && (
            <>
              <div className="mt-3">
                <h5 className="text-muted">{`Members of ${selected_recovery_group.name} Group`}</h5>
                <GroupMembersTable recovery_group={selected_recovery_group} />
              </div>
              <span>Approvers required for recovery: <strong>{`${selected_recovery_group.required_approvers}`}</strong></span>
              <Toast bg="warning" show={true} className="mt-3">
                <Toast.Body>
                  {warning_msg_recovery_group}
                </Toast.Body>
              </Toast> 
            </>
          )}
          <div className="mt-4">
            <p><strong>Notice:</strong> By assigning the recovery group to the selected users, you are protecting their accounts against accidental loss or theft.</p>
          </div>
          <div className="btn-panel">
            <Button disabled={!selected_recovery_group} onClick={() => assignSelectedRecoveryGroup()}>Set Recovery Group</Button>
            <Button variant="no-outline-primary" onClick={handleCancel}>Cancel</Button>
          </div>
        </> :
        <>
          <p className="no-recovery-group">No recovery groups have been created yet</p>
          <Button variant="outline-secondary" size="sm" onClick={navigateToApprovalGroup}>Create a Recovery Group</Button>
        </>
      }
    </Modal.Body>
  </Modal>;
}

export default React.memo(SetRecoveryGroupModalComponent);
